/**
 * Um lote de regra do catálogo (spec 2026-10-18 D4/D9, consoleai#25239): o estado de
 * cada WAF e, para os que estão em observação, quantas requisições casaram a regra
 * em 7 dias. A promoção a Block é escolha por WAF; vem marcado só o que não casou
 * nada, e casar não diz se o tráfego era legítimo — as amostras estão no WAF.
 */
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Alert, Anchor, Badge, Button, Checkbox, Group, Loader, Stack, Table, Text,
  Textarea } from '@mantine/core'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatApiError } from '@/api/errors'
import { getWafRulesObservation, lerLote, promoverLote, type EstadoDoItem, type ItemDoLote,
  type WafRuleObservation } from '@/api/waf'

const NOME_DA_REGRA: Record<string, string> = { 'block-phpinfo': 'Block-phpinfo' }

const ESTADO: Record<EstadoDoItem, { texto: string; cor: string }> = {
  pendente: { texto: 'Na fila', cor: 'gray' },
  observando: { texto: 'Em observação (Count)', cor: 'blue' },
  promovendo: { texto: 'Promovendo', cor: 'gray' },
  bloqueando: { texto: 'Bloqueando', cor: 'teal' },
  aguardando_gmud: { texto: 'Aguardando GMUD', cor: 'yellow' },
  ja_tem: { texto: 'Já tinha a regra', cor: 'gray' },
  falhou: { texto: 'Falhou', cor: 'red' },
}

/** Itens em andamento fazem a página se atualizar sozinha. */
export function emAndamento(itens: ItemDoLote[]): boolean {
  return itens.some((i) => i.estado === 'pendente' || i.estado === 'promovendo')
}

/** Vem marcado para promoção só o item observado com zero casamentos. */
export function marcadosDeInicio(itens: ItemDoLote[],
  obs: Record<number, WafRuleObservation | undefined>): Set<number> {
  return new Set(itens.filter((i) => i.estado === 'observando'
    && obs[i.managed_waf_id] !== undefined && obs[i.managed_waf_id]?.counted === 0)
    .map((i) => i.managed_waf_id))
}

export function ItensDoLote({ itens, obs, selecionados, onAlternar }: {
  itens: ItemDoLote[]
  obs: Record<number, WafRuleObservation | undefined>
  selecionados: Set<number>
  onAlternar: (wafId: number) => void
}) {
  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Promover</Table.Th>
          <Table.Th>WAF</Table.Th>
          <Table.Th>Estado</Table.Th>
          <Table.Th>Casou em 7 dias</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {itens.map((i) => {
          const o = obs[i.managed_waf_id]
          return (
            <Table.Tr key={i.id}>
              <Table.Td>
                {i.estado === 'observando' && (
                  <Checkbox
                    aria-label={`Promover ${i.nome}`}
                    checked={selecionados.has(i.managed_waf_id)}
                    onChange={() => onAlternar(i.managed_waf_id)}
                  />
                )}
              </Table.Td>
              <Table.Td>
                <Anchor component={Link} to={`/security/waf/${i.managed_waf_id}`} size="sm" ff="monospace">
                  {i.nome}
                </Anchor>
              </Table.Td>
              <Table.Td>
                <Group gap={6}>
                  <Badge color={ESTADO[i.estado].cor} variant="light">{ESTADO[i.estado].texto}</Badge>
                  {i.gmud_id && <Text size="xs" c="dimmed">GMUD {i.gmud_id}</Text>}
                  {i.erro && <Text size="xs" c="red">{i.erro}</Text>}
                </Group>
              </Table.Td>
              <Table.Td>
                {i.estado !== 'observando' ? null
                  : o === undefined ? <Text size="xs" c="dimmed">medindo…</Text>
                  : <Text size="sm" ff="monospace">{o.counted}</Text>}
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

export function WafLotePage() {
  const { loteId } = useParams()
  const id = Number(loteId)
  const qc = useQueryClient()
  const lote = useQuery({
    queryKey: ['waf-lote', id],
    queryFn: () => lerLote(id),
    enabled: Number.isFinite(id),
    refetchInterval: (q) => (q.state.data && emAndamento(q.state.data.itens) ? 5000 : false),
  })
  const itens = lote.data?.itens ?? []
  const nomeDaRegra = NOME_DA_REGRA[lote.data?.regra ?? ''] ?? lote.data?.regra ?? ''
  const observando = itens.filter((i) => i.estado === 'observando')
  const observacoes = useQueries({
    queries: observando.map((i) => ({
      queryKey: ['waf-observacao', i.managed_waf_id],
      queryFn: () => getWafRulesObservation(i.managed_waf_id, 7),
    })),
  })
  const obs: Record<number, WafRuleObservation | undefined> = {}
  observando.forEach((i, k) => { obs[i.managed_waf_id] = observacoes[k]?.data?.rules?.[nomeDaRegra] })
  // `useQueries` devolve array novo a cada render: a marcação inicial depende de
  // uma assinatura estável, senão o efeito re-renderiza em laço.
  const assinatura = observando.map((i) => `${i.managed_waf_id}:${obs[i.managed_waf_id]?.counted ?? '?'}`).join(',')

  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [tocado, setTocado] = useState(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!tocado) setSelecionados(marcadosDeInicio(itens, obs)) }, [assinatura, tocado])
  const [motivo, setMotivo] = useState('')
  const promover = useMutation({
    mutationFn: () => promoverLote(id, [...selecionados], motivo.trim()),
    onSuccess: () => { setTocado(false); setMotivo(''); qc.invalidateQueries({ queryKey: ['waf-lote', id] }) },
  })
  const alternar = (wafId: number) => {
    setTocado(true)
    setSelecionados((s) => {
      const n = new Set(s)
      if (n.has(wafId)) n.delete(wafId)
      else n.add(wafId)
      return n
    })
  }

  if (lote.isLoading) return <Loader />
  if (lote.error) return <Alert color="red">{formatApiError(lote.error)}</Alert>
  if (!lote.data) return null
  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Anchor component={Link} to="/security/waf" size="sm">Registro de WAF</Anchor>
        <Text size="lg" fw={700}>Lote {lote.data.id}: {nomeDaRegra}</Text>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>{lote.data.motivo}</Text>
      </Stack>
      <Text size="sm">
        A contagem é das requisições que casaram a regra em Count. Ela não diz se o
        tráfego era legítimo: antes de promover um WAF com contagem acima de zero, abra
        o WAF e leia as amostras da regra.
      </Text>
      <ItensDoLote itens={itens} obs={obs} selecionados={selecionados} onAlternar={alternar} />
      {observando.length > 0 && (
        <Stack gap="sm">
          <Textarea
            label="Motivo da promoção"
            value={motivo}
            onChange={(e) => setMotivo(e.currentTarget.value)}
            minRows={2}
          />
          {promover.error && <Alert color="red">{formatApiError(promover.error)}</Alert>}
          <Group justify="flex-end">
            <Button
              color="red"
              onClick={() => promover.mutate()}
              loading={promover.isPending}
              disabled={selecionados.size === 0 || motivo.trim().length < 5}
            >
              Promover {selecionados.size} WAF(s) a Block
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  )
}
